import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Avatar,
  Tooltip,
  CircularProgress,
  TablePagination,
  Chip,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as ConnectionsIcon,
  Merge as MergeIcon,
  Search as SearchIcon,
  AutoAwesome as AutoAwesomeIcon,
  RateReview as ReviewIcon,
} from '@mui/icons-material';
import { connectionsApi, offlinePlayersApi, mergeRequestsApi } from '../api';
import type { PlayerConnectionWithStats, UpdateConnectionInput, OfflinePlayer, MergeRequest } from '../types';
import { renderIcon, GlobalMergeConfirmDialog, GlobalMergeReviewDialog } from '../components';
import { ConnectionsEducationModal } from '../components/education';
import { hasSeenEducationModal } from '../lib/educationModals';
import { useDocumentTitle, usePagination } from '../hooks/core';

const SIMILARITY_THRESHOLD = 0.9; // 90% match threshold for suggestions

/**
 * Calculate similarity between two names using character frequency overlap.
 */
function calculateNameSimilarity(name1: string, name2: string): number {
  const n1 = name1.toLowerCase().trim();
  const n2 = name2.toLowerCase().trim();

  if (n1 === n2) return 1.0;
  if (!n1 || !n2) return 0.0;

  const chars1 = new Map<string, number>();
  const chars2 = new Map<string, number>();

  for (const c of n1) {
    if (c !== ' ') chars1.set(c, (chars1.get(c) || 0) + 1);
  }
  for (const c of n2) {
    if (c !== ' ') chars2.set(c, (chars2.get(c) || 0) + 1);
  }

  let overlap = 0;
  for (const [c, count1] of chars1) {
    const count2 = chars2.get(c) || 0;
    overlap += Math.min(count1, count2);
  }

  const len1 = [...n1].filter(c => c !== ' ').length;
  const len2 = [...n2].filter(c => c !== ' ').length;
  const maxLen = Math.max(len1, len2);

  if (maxLen === 0) return 0.0;
  return overlap / maxLen;
}

export const ConnectionsPage: React.FC = () => {
  useDocumentTitle('Connections');

  const [connections, setConnections] = useState<PlayerConnectionWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { paginationProps, resetPage, getPaginatedItems } = usePagination();

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState<PlayerConnectionWithStats | null>(null);
  const [nickname, setNickname] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  // Merge flow state (outgoing - sending merge requests)
  const [mergeSelectOpen, setMergeSelectOpen] = useState(false);
  const [mergeTargetConnection, setMergeTargetConnection] = useState<PlayerConnectionWithStats | null>(null);
  const [offlinePlayers, setOfflinePlayers] = useState<OfflinePlayer[]>([]);
  const [loadingOfflinePlayers, setLoadingOfflinePlayers] = useState(false);
  const [selectedOfflinePlayer, setSelectedOfflinePlayer] = useState<OfflinePlayer | null>(null);
  const [mergeConfirmOpen, setMergeConfirmOpen] = useState(false);
  const [mergeSuccess, setMergeSuccess] = useState<string | null>(null);
  const [offlinePlayerSearch, setOfflinePlayerSearch] = useState('');

  // Review flow state (incoming - reviewing merge requests from others)
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingIncomingRequests, setPendingIncomingRequests] = useState<MergeRequest[]>([]);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewGlobalRequestId, setReviewGlobalRequestId] = useState<string | null>(null);

  // Education modal state
  const [educationModalOpen, setEducationModalOpen] = useState(() => !hasSeenEducationModal('connections'));

  const fetchConnections = useCallback(async () => {
    try {
      setLoading(true);
      const data = await connectionsApi.list();
      setConnections(data);
      setError(null);
    } catch (_err) {
      setError('Failed to load connections');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch pending incoming global merge requests targeting current user
  const fetchPendingIncomingRequests = useCallback(async () => {
    try {
      const requests = await mergeRequestsApi.getAllPending();
      // Filter for parent global requests (isGlobal=true, no boardId)
      const globalParents = requests.filter(r => r.isGlobal && !r.boardId);
      setPendingIncomingRequests(globalParents);
    } catch (err) {
      // Silently fail - this is a secondary feature
      console.error('Failed to fetch pending incoming requests:', err);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
    fetchPendingIncomingRequests();
  }, [fetchConnections, fetchPendingIncomingRequests]);

  // Handle review query param from notification links
  useEffect(() => {
    const reviewId = searchParams.get('review');
    if (reviewId) {
      setReviewGlobalRequestId(reviewId);
      setReviewDialogOpen(true);
      // Clear the query param from URL
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const getConnectionName = (conn: PlayerConnectionWithStats) => {
    if (conn.nickname) return conn.nickname;
    if (conn.connectedUser) return conn.connectedUser.displayName;
    return 'Unknown';
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Filter connections by search query
  const filteredConnections = useMemo(() => {
    if (!searchQuery.trim()) return connections;
    const query = searchQuery.toLowerCase().trim();
    return connections.filter(conn => {
      const name = getConnectionName(conn).toLowerCase();
      const handle = (conn.connectedUser?.handle || '').toLowerCase();
      return name.includes(query) || handle.includes(query);
    });
  }, [connections, searchQuery]);

  // Paginated connections
  const paginatedConnections = getPaginatedItems(filteredConnections);

  // Handle search change (reset to first page)
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    resetPage();
  };

  // Map pending incoming requests by requestedById for quick lookup
  const pendingRequestByRequester = useMemo(() => {
    const map = new Map<string, MergeRequest>();
    for (const req of pendingIncomingRequests) {
      map.set(req.requestedById, req);
    }
    return map;
  }, [pendingIncomingRequests]);

  // Compute suggested matches and filtered offline players for the merge dialog
  const targetName = mergeTargetConnection ? getConnectionName(mergeTargetConnection) : '';

  const { suggestedPlayers, otherPlayers } = useMemo(() => {
    if (!targetName || offlinePlayers.length === 0) {
      return { suggestedPlayers: [] as Array<OfflinePlayer & { similarity: number }>, otherPlayers: offlinePlayers };
    }

    const suggested: Array<OfflinePlayer & { similarity: number }> = [];
    const others: OfflinePlayer[] = [];

    for (const player of offlinePlayers) {
      const similarity = calculateNameSimilarity(player.displayName, targetName);
      if (similarity >= SIMILARITY_THRESHOLD) {
        suggested.push({ ...player, similarity });
      } else {
        others.push(player);
      }
    }

    // Sort suggested by similarity (highest first)
    suggested.sort((a, b) => b.similarity - a.similarity);

    return { suggestedPlayers: suggested, otherPlayers: others };
  }, [targetName, offlinePlayers]);

  // Filter other players by search query
  const filteredOtherPlayers = useMemo(() => {
    if (!offlinePlayerSearch.trim()) return otherPlayers;
    const query = offlinePlayerSearch.toLowerCase().trim();
    return otherPlayers.filter(p => p.displayName.toLowerCase().includes(query));
  }, [otherPlayers, offlinePlayerSearch]);

  const handleOpenEditDialog = (conn: PlayerConnectionWithStats) => {
    setEditingConnection(conn);
    setNickname(conn.nickname || '');
    setNotes(conn.notes || '');
    setDialogError(null);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingConnection(null);
    setNickname('');
    setNotes('');
    setDialogError(null);
  };

  const handleSave = async () => {
    if (!editingConnection) return;

    setSaving(true);
    setDialogError(null);

    try {
      const input: UpdateConnectionInput = {};
      if (nickname !== (editingConnection.nickname || '')) {
        input.nickname = nickname.trim() || undefined;
      }
      if (notes !== (editingConnection.notes || '')) {
        input.notes = notes.trim() || undefined;
      }

      await connectionsApi.update(editingConnection.id, input);
      handleCloseEditDialog();
      fetchConnections();
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Failed to save connection');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (conn: PlayerConnectionWithStats) => {
    const name = getConnectionName(conn);
    if (!confirm(`Remove "${name}" from your connections?`)) {
      return;
    }

    try {
      await connectionsApi.delete(conn.id);
      fetchConnections();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete connection');
    }
  };

  // Open merge dialog - fetch offline players and show selection
  const handleOpenMergeDialog = async (conn: PlayerConnectionWithStats) => {
    setMergeTargetConnection(conn);
    setMergeSelectOpen(true);
    setLoadingOfflinePlayers(true);
    setSelectedOfflinePlayer(null);

    try {
      const players = await offlinePlayersApi.getMyOfflinePlayers();
      setOfflinePlayers(players);
    } catch (_err) {
      setError('Failed to load offline players');
      setMergeSelectOpen(false);
    } finally {
      setLoadingOfflinePlayers(false);
    }
  };

  const handleCloseMergeSelect = () => {
    setMergeSelectOpen(false);
    setMergeTargetConnection(null);
    setSelectedOfflinePlayer(null);
    setOfflinePlayers([]);
    setOfflinePlayerSearch('');
  };

  const handleSelectOfflinePlayer = (player: OfflinePlayer) => {
    setSelectedOfflinePlayer(player);
    setMergeSelectOpen(false);
    setMergeConfirmOpen(true);
  };

  const handleMergeCreated = (result: { requests: unknown[] }) => {
    const boardCount = result.requests.length;
    setMergeSuccess(
      `Merge request sent! ${getConnectionName(mergeTargetConnection!)} will need to approve the merge across ${boardCount} board${boardCount !== 1 ? 's' : ''}.`
    );
    setMergeConfirmOpen(false);
    setSelectedOfflinePlayer(null);
    setMergeTargetConnection(null);
  };

  const handleCloseMergeConfirm = () => {
    setMergeConfirmOpen(false);
    setSelectedOfflinePlayer(null);
    setMergeTargetConnection(null);
  };

  // Review flow handlers
  const handleOpenReviewDialog = (globalRequestId: string) => {
    setReviewGlobalRequestId(globalRequestId);
    setReviewDialogOpen(true);
  };

  const handleCloseReviewDialog = () => {
    setReviewDialogOpen(false);
    setReviewGlobalRequestId(null);
  };

  const handleReviewApproved = () => {
    setMergeSuccess('Merge request approved! All squares have been transferred to your account.');
    setReviewDialogOpen(false);
    setReviewGlobalRequestId(null);
    // Refresh the pending requests list
    fetchPendingIncomingRequests();
  };

  const handleReviewDenied = () => {
    setMergeSuccess('Merge request denied.');
    setReviewDialogOpen(false);
    setReviewGlobalRequestId(null);
    // Refresh the pending requests list
    fetchPendingIncomingRequests();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <ConnectionsIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" component="h1">
              Connections
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Players you frequently work with. Connections are automatically created when you manage boards together.
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {mergeSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setMergeSuccess(null)}>
          {mergeSuccess}
        </Alert>
      )}

      {/* Alert for pending incoming merge requests */}
      {pendingIncomingRequests.length > 0 && (
        <Alert
          severity="warning"
          icon={<ReviewIcon />}
          sx={{ mb: 3 }}
          action={
            pendingIncomingRequests.length === 1 ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => handleOpenReviewDialog(pendingIncomingRequests[0].id)}
              >
                Review
              </Button>
            ) : undefined
          }
        >
          <Typography variant="body2">
            {pendingIncomingRequests.length === 1
              ? `You have a pending merge request from ${pendingIncomingRequests[0].requestedBy?.displayName || 'a board manager'}.`
              : `You have ${pendingIncomingRequests.length} pending merge requests.`}
          </Typography>
        </Alert>
      )}

      {connections.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ConnectionsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No connections yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Connections are automatically created when players participate on multiple boards you manage,
            or when merge requests are approved.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Search field */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {filteredConnections.length} connection{filteredConnections.length !== 1 ? 's' : ''}
              {searchQuery.trim() && ` (filtered from ${connections.length})`}
            </Typography>
            <TextField
              size="small"
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              sx={{ ml: 'auto', minWidth: 180 }}
              InputProps={{
                sx: { height: 32 },
              }}
            />
          </Box>

          {/* Connections table */}
          {filteredConnections.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No connections found matching "{searchQuery}"
              </Typography>
            </Paper>
          ) : (
            <>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Player</th>
                      <th style={{ textAlign: 'center', padding: '12px', fontWeight: 600, width: 100 }}>Shared Boards</th>
                      <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600, width: 180 }}>Last Board</th>
                      <th style={{ textAlign: 'center', padding: '12px', fontWeight: 600, width: 100 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedConnections.map((conn) => {
                      const name = getConnectionName(conn);
                      const icon = conn.connectedUser?.iconName;
                      const handle = conn.connectedUser?.handle;

                      return (
                        <tr
                          key={conn.id}
                          style={{
                            borderBottom: '1px solid #e0e0e0',
                            backgroundColor: '#fff',
                          }}
                        >
                          {/* Player Name */}
                          <td style={{ padding: '12px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar
                                variant="rounded"
                                sx={{
                                  width: 40,
                                  height: 40,
                                  bgcolor: 'primary.main',
                                  borderRadius: 1,
                                }}
                              >
                                {icon ? renderIcon(icon, 20) : getInitials(name)}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {name}
                                </Typography>
                                {handle && (
                                  <Typography variant="caption" color="text.secondary">
                                    {handle}
                                  </Typography>
                                )}
                                {conn.notes && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {conn.notes}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </td>

                          {/* Shared Boards */}
                          <td style={{ textAlign: 'center', padding: '12px' }}>
                            <Typography variant="body2" color="text.secondary">
                              {conn.sharedBoardCount}
                            </Typography>
                          </td>

                          {/* Last Board */}
                          <td style={{ padding: '12px' }}>
                            {conn.lastBoardName ? (
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {conn.lastBoardName}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                —
                              </Typography>
                            )}
                          </td>

                          {/* Actions */}
                          <td style={{ textAlign: 'center', padding: '12px' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                              {/* Review button for pending incoming merge requests */}
                              {conn.connectedUserId && pendingRequestByRequester.get(conn.connectedUserId) && (
                                <Tooltip title="Review merge request">
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    aria-label="Review merge request"
                                    onClick={() => {
                                      const req = pendingRequestByRequester.get(conn.connectedUserId!);
                                      if (req) handleOpenReviewDialog(req.id);
                                    }}
                                  >
                                    <ReviewIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {conn.connectedUserId && (
                                <Tooltip title="Merge offline player into this connection">
                                  <IconButton size="small" color="primary" aria-label="Merge" onClick={() => handleOpenMergeDialog(conn)}>
                                    <MergeIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Edit">
                                <IconButton size="small" aria-label="Edit" onClick={() => handleOpenEditDialog(conn)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove">
                                <IconButton
                                  size="small"
                                  color="error"
                                  aria-label="Remove"
                                  onClick={() => handleDelete(conn)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
              <TablePagination
                component="div"
                count={filteredConnections.length}
                {...paginationProps}
              />
            </>
          )}
        </>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          Edit Connection
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {editingConnection && getConnectionName(editingConnection)}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Nickname (optional)"
            fullWidth
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="A friendly name for this player"
          />
          <TextField
            margin="dense"
            label="Notes (optional)"
            fullWidth
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any notes about this connection"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Offline Player Selection Dialog */}
      <Dialog open={mergeSelectOpen} onClose={handleCloseMergeSelect} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MergeIcon color="primary" />
            <Typography variant="h6" component="span">
              Select Offline Player to Merge
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {mergeTargetConnection && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Select an offline player to merge into <strong>{getConnectionName(mergeTargetConnection)}</strong>
            </Alert>
          )}
          {loadingOfflinePlayers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : offlinePlayers.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              You don't have any offline players to merge.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Suggested Matches Section */}
              {suggestedPlayers.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AutoAwesomeIcon color="warning" sx={{ fontSize: 20 }} />
                    <Typography variant="subtitle2" color="warning.main">
                      Suggested Match{suggestedPlayers.length !== 1 ? 'es' : ''}
                    </Typography>
                  </Box>
                  {suggestedPlayers.map((player) => (
                    <Paper
                      key={player.id}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        bgcolor: 'warning.50',
                        border: '2px solid',
                        borderColor: 'warning.main',
                        '&:hover': { bgcolor: 'warning.100' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                      }}
                      onClick={() => handleSelectOfflinePlayer(player)}
                    >
                      <Avatar
                        variant="rounded"
                        sx={{ width: 40, height: 40, bgcolor: 'warning.main' }}
                      >
                        {getInitials(player.displayName)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {player.displayName}
                          </Typography>
                          <Chip
                            label={`${Math.round(player.similarity * 100)}% match`}
                            size="small"
                            color="warning"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {player.boardCount} board{player.boardCount !== 1 ? 's' : ''} • {player.totalSquares} square{player.totalSquares !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                  {otherPlayers.length > 0 && <Divider sx={{ my: 2 }} />}
                </>
              )}

              {/* Other Players Section with Search */}
              {otherPlayers.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {suggestedPlayers.length > 0 ? 'Other Players' : 'All Players'} ({otherPlayers.length})
                    </Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Search offline players..."
                    value={offlinePlayerSearch}
                    onChange={(e) => setOfflinePlayerSearch(e.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ color: 'text.disabled', mr: 1, fontSize: 20 }} />,
                      sx: { height: 36 },
                    }}
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                    {filteredOtherPlayers.length === 0 ? (
                      <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        No players found matching "{offlinePlayerSearch}"
                      </Typography>
                    ) : (
                      filteredOtherPlayers.map((player) => (
                        <Paper
                          key={player.id}
                          sx={{
                            p: 2,
                            mb: 1,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                          }}
                          onClick={() => handleSelectOfflinePlayer(player)}
                        >
                          <Avatar
                            variant="rounded"
                            sx={{ width: 40, height: 40, bgcolor: 'grey.400' }}
                          >
                            {getInitials(player.displayName)}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {player.displayName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {player.boardCount} board{player.boardCount !== 1 ? 's' : ''} • {player.totalSquares} square{player.totalSquares !== 1 ? 's' : ''}
                            </Typography>
                          </Box>
                        </Paper>
                      ))
                    )}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMergeSelect}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Global Merge Confirm Dialog */}
      {mergeTargetConnection && mergeTargetConnection.connectedUserId && (
        <GlobalMergeConfirmDialog
          open={mergeConfirmOpen}
          offlinePlayer={selectedOfflinePlayer}
          targetUserId={mergeTargetConnection.connectedUserId}
          targetUserName={getConnectionName(mergeTargetConnection)}
          onClose={handleCloseMergeConfirm}
          onMergeCreated={handleMergeCreated}
        />
      )}

      {/* Global Merge Review Dialog (for incoming requests) */}
      <GlobalMergeReviewDialog
        open={reviewDialogOpen}
        globalRequestId={reviewGlobalRequestId}
        onClose={handleCloseReviewDialog}
        onApproved={handleReviewApproved}
        onDenied={handleReviewDenied}
      />

      {/* Connections Education Modal */}
      <ConnectionsEducationModal
        open={educationModalOpen}
        onClose={() => setEducationModalOpen(false)}
      />
    </Box>
  );
};
